import './InputsPanel.scss';
import { useEffect, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import {
  SlideInputGroup,
  ParameterConfigurationType,
  SlideInput
} from '@blisspointmedia/bpm-types/dist/SlidesV2';
import { evaluateConstraints } from '@blisspointmedia/bpm-types/dist/SlidesV2Rules';
import { LAGS } from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { MdCheck } from 'react-icons/md';
import { DatePicker, DateConfig } from '../../Components/DatePicker/DatePicker';
import { Dropdown } from '../../Components/Dropdown';
import { Placement, Size } from '../../Components/designConstants';
import { MetricDropdown } from '../../Components/MetricDropdown';
import {
  getAllCurrentValues,
  isMetricAllowed,
  isOptionAllowed,
  isSourceAllowed,
  resolveParameterValue,
  SlideInstance
} from './utils';

const LAG_OPTIONS = LAGS.map(l => ({ value: l, label: l }));

type ConstraintChange =
  { kind: 'cleared'; from: string } | { kind: 'multiRemoved'; removed: string[] };

/** Payload for when an input is cleared due to a constraint/rule effect. */
interface ConstraintClearedParameterValuePayload {
  parameterLabel: string;
  description?: string;
  change?: ConstraintChange;
}

interface InputsPanelProps {
  inputGroups: SlideInputGroup[];
  // Initial values for this slide
  currentSlideInitValues: Record<string, unknown>;
  // Pending edits for this slide
  currentSlideEdits: Record<string, unknown>;
  activeSlide: SlideInstance | null;
  handleChange: (slideId: string, key: string, value: unknown) => void;
  // Called when an input is cleared due to a constraint/rule effect.
  onConstraintClearedParameterValue?: (payload: ConstraintClearedParameterValuePayload) => void;
}

export const InputsPanel = ({
  inputGroups,
  currentSlideInitValues,
  currentSlideEdits,
  activeSlide,
  handleChange,
  onConstraintClearedParameterValue
}: InputsPanelProps) => {
  if (!activeSlide) {
    return null;
  }
  const { key, parameters } = activeSlide;

  return (
    <div className='inputs-panel'>
      <div className='inputs-content'>
        {inputGroups.length === 0 && (
          <div className='no-inputs-message'>
            <div className='icon'>
              <MdCheck />
            </div>
            <div className='message'>No Inputs Required</div>
            <div className='sub-message'>
              This is a text only slide that you can edit natively in your deck after generating.
            </div>
          </div>
        )}

        {inputGroups.map(group => {
          // Get parameters for this grouping
          const inputIds = group.inputIds || [];

          const parametersForGroup: SlideInput[] = [];
          for (const inputId of inputIds) {
            const param = parameters.find(p => p.inputId === inputId);
            if (param) {
              parametersForGroup.push(param);
            }
          }

          return (
            <InputGroupSection
              key={group.groupId}
              label={group.label}
              parameters={parametersForGroup}
              currentSlideEdits={currentSlideEdits}
              currentSlideInitValues={currentSlideInitValues}
              onChange={(parameterName, val) => handleChange(key, parameterName, val)}
              onConstraintClearedParameterValue={onConstraintClearedParameterValue}
            />
          );
        })}
      </div>
    </div>
  );
};

interface InputGroupSectionProps {
  label: string;
  parameters: SlideInput[];
  currentSlideEdits: Record<string, unknown>;
  currentSlideInitValues: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  onConstraintClearedParameterValue?: (payload: ConstraintClearedParameterValuePayload) => void;
}

const InputGroupSection = ({
  label,
  parameters,
  currentSlideEdits,
  currentSlideInitValues,
  onChange,
  onConstraintClearedParameterValue
}: InputGroupSectionProps) => {
  const allCurrentValues = useMemo(
    () => getAllCurrentValues(currentSlideEdits, currentSlideInitValues),
    [currentSlideEdits, currentSlideInitValues]
  );

  useEffect(() => {
    autoClearInvalidValues(
      parameters,
      allCurrentValues,
      onChange,
      onConstraintClearedParameterValue
    );
  }, [allCurrentValues, parameters, onChange, onConstraintClearedParameterValue]);

  return (
    <div className='input-card'>
      <div className='card-title'>{label}</div>
      <div className='card-body'>
        {parameters?.map(parameter => {
          const value = resolveParameterValue(
            parameter.key,
            currentSlideEdits,
            currentSlideInitValues
          );
          const effects = evaluateConstraints(parameter.constraints, allCurrentValues);

          // Skip rendering hidden inputs
          if (effects.hidden) {
            return null;
          }

          if (parameter.type === ParameterConfigurationType.PRIMARY_ONLY_DATE_PICKER) {
            const dateValue = value as DateConfig | undefined;
            const dateConfig: DateConfig | undefined = dateValue
              ? {
                  start: dateValue.start,
                  end: dateValue.end,
                  selectedPreset: dateValue.selectedPreset,
                  startCustomPreset: dateValue.startCustomPreset,
                  endCustomPreset: dateValue.endCustomPreset,
                  comparisonStart: dateValue.comparisonStart,
                  comparisonEnd: dateValue.comparisonEnd,
                  selectedCompPerType: dateValue.selectedCompPerType,
                  startCompCustomPreset: dateValue.startCompCustomPreset,
                  endCompCustomPreset: dateValue.endCompCustomPreset
                }
              : undefined;

            return (
              <div key={parameter.key} className='form-row'>
                <div className='label'>{parameter.label}</div>
                <DatePicker
                  range={
                    dateValue?.start && dateValue?.end
                      ? { start: dateValue.start, end: dateValue.end }
                      : undefined
                  }
                  onChange={(
                    dateInputs,
                    { selectedPreset, startCustomPreset, endCustomPreset }
                  ) => {
                    onChange(parameter.key, {
                      start: dateInputs.start,
                      end: dateInputs.end,
                      selectedPreset,
                      startCustomPreset,
                      endCustomPreset
                    });
                  }}
                  removeHeaderText
                  datePresets
                  dateConfig={dateConfig}
                />
              </div>
            );
          }

          if (parameter.type === ParameterConfigurationType.PRIMARY_AND_COMPARISON_DATE_PICKER) {
            const dateValue = value as DateConfig | undefined;
            const dateConfig: DateConfig | undefined = dateValue
              ? {
                  start: dateValue.start,
                  end: dateValue.end,
                  selectedPreset: dateValue.selectedPreset,
                  startCustomPreset: dateValue.startCustomPreset,
                  endCustomPreset: dateValue.endCustomPreset,
                  comparisonStart: dateValue.comparisonStart,
                  comparisonEnd: dateValue.comparisonEnd,
                  startCompCustomPreset: dateValue.startCompCustomPreset,
                  endCompCustomPreset: dateValue.endCompCustomPreset,
                  selectedCompPerType: dateValue.selectedCompPerType
                }
              : undefined;

            return (
              <div key={parameter.key} className='form-row'>
                <div className='label'>{parameter.label}</div>
                <DatePicker
                  comparison
                  range={
                    dateValue?.start && dateValue?.end
                      ? { start: dateValue.start, end: dateValue.end }
                      : undefined
                  }
                  comparisonRange={
                    dateValue?.comparisonStart && dateValue?.comparisonEnd
                      ? { start: dateValue.comparisonStart, end: dateValue.comparisonEnd }
                      : undefined
                  }
                  onChange={() => {}}
                  comparisonOnChange={() => {}}
                  primaryAndComparisonOnChange={newDateInfo => {
                    onChange(parameter.key, newDateInfo);
                  }}
                  removeHeaderText
                  datePresets
                  dateConfig={dateConfig}
                />
              </div>
            );
          }

          if (parameter.type === ParameterConfigurationType.TEXT_BOX) {
            return (
              <div key={parameter.key} className='form-row'>
                <div className='label'>{parameter.label}</div>
                <Form.Control
                  placeholder='Enter text'
                  value={(value as string) ?? ''}
                  onChange={e => onChange(parameter.key, e.target.value)}
                />
              </div>
            );
          }

          if (parameter.type === ParameterConfigurationType.SINGLE_SELECT_DROPDOWN) {
            const optsWithDisabled = optionsWithDisabled(
              parameter.options || [],
              effects.allowedValues,
              effects.disallowedValues
            );
            return (
              <div key={parameter.key} className='form-row'>
                <div className='label'>{parameter.label}</div>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  options={optsWithDisabled}
                  onChange={v => onChange(parameter.key, v)}
                  value={value as string}
                  size={Size.LG}
                />
              </div>
            );
          }

          if (parameter.type === ParameterConfigurationType.METRIC_CONFIG_DROPDOWN) {
            const metricsWithDisabled = optionsWithDisabled(
              parameter?.options?.metrics || [],
              effects.allowedMetricConfigOptions?.metrics,
              effects.disallowedMetricConfigOptions?.metrics
            );
            const sourcesWithDisabled = optionsWithDisabled(
              parameter?.options?.sources || [],
              effects.allowedMetricConfigOptions?.sources,
              effects.disallowedMetricConfigOptions?.sources
            );
            const kpisWithDisabled = optionsWithDisabled(
              parameter?.options?.kpis || [],
              effects.allowedMetricConfigOptions?.kpis,
              effects.disallowedMetricConfigOptions?.kpis
            );
            // `withLag` is set by the backend's metric config factories for blueprints on lag-supported channels.
            const withLag = parameter.withLag === true;
            return (
              <div key={parameter.key} className='form-row'>
                <div className='label'>{parameter.label}</div>
                <MetricDropdown
                  value={value as MetricConfigValue}
                  onChange={v => onChange(parameter.key, v)}
                  metrics={metricsWithDisabled}
                  kpis={kpisWithDisabled}
                  sources={sourcesWithDisabled}
                  optionalPane='source'
                  showLag={withLag}
                  lags={LAG_OPTIONS}
                />
              </div>
            );
          }

          if (parameter.type === ParameterConfigurationType.METRIC_CONFIG_WITHOUT_SOURCE_DROPDOWN) {
            const metricsWithDisabled = optionsWithDisabled(
              parameter?.options?.metrics || [],
              effects.allowedMetricConfigOptions?.metrics,
              effects.disallowedMetricConfigOptions?.metrics
            );
            const kpisWithDisabled = optionsWithDisabled(
              parameter?.options?.kpis || [],
              effects.allowedMetricConfigOptions?.kpis,
              effects.disallowedMetricConfigOptions?.kpis
            );
            const withLag = parameter.withLag === true;
            return (
              <div key={parameter.key} className='form-row'>
                <div className='label'>{parameter.label}</div>
                <MetricDropdown
                  value={value as MetricConfigValue}
                  onChange={v => onChange(parameter.key, v)}
                  metrics={metricsWithDisabled}
                  kpis={kpisWithDisabled}
                  showLag={withLag}
                  lags={LAG_OPTIONS}
                />
              </div>
            );
          }

          return (
            <div key={parameter.key} className='form-row'>
              <div className='label'>{parameter.label}</div>
              <Form.Control
                placeholder='Enter text'
                value={(value as string) ?? ''}
                onChange={e => onChange(parameter.key, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface MetricConfigValue {
  metric: string;
  kpi: string | null;
  source?: string | null;
  lag?: string | null;
}

interface OptionItem {
  label: string;
  value: string;
}

export type OptionItemWithDisabled = OptionItem & { disabled?: boolean };

/**
 * Returns all options with disabled: true for options not in allowed or in disallowed.
 * Keeps options visible but greyed out / unselectable instead of hiding them.
 */
const optionsWithDisabled = (
  options: OptionItem[],
  allowed?: string[],
  disallowed?: string[]
): OptionItemWithDisabled[] =>
  options.map(opt => ({
    ...opt,
    disabled: !isOptionAllowed(opt.value, allowed, disallowed) || undefined
  }));

const labelForValue = (options: OptionItem[] | undefined, value: string): string =>
  options?.find(opt => opt.value === value)?.label ?? value;

/**
 * Auto-clear parameter values that become invalid due to constraint effects.
 * Handles metric config dropdowns, single/multi-select dropdowns, and hidden parameters.
 */
const autoClearInvalidValues = (
  parameters: SlideInput[],
  allCurrentValues: Record<string, unknown>,
  onChange: (key: string, value: unknown) => void,
  onConstraintClearedParameterValue?: (payload: ConstraintClearedParameterValuePayload) => void
): void => {
  for (const parameter of parameters) {
    if (!parameter.constraints || parameter.constraints.length === 0) {
      continue;
    }

    const effects = evaluateConstraints(parameter.constraints, allCurrentValues);
    const currentValue = allCurrentValues[parameter.key];

    if (!currentValue) {
      continue;
    }

    if (
      parameter.type === ParameterConfigurationType.METRIC_CONFIG_DROPDOWN ||
      parameter.type === ParameterConfigurationType.METRIC_CONFIG_WITHOUT_SOURCE_DROPDOWN
    ) {
      const metricConfig = currentValue as MetricConfigValue;
      const { metric, source } = metricConfig;
      const metricInvalid = Boolean(metric) && !isMetricAllowed(metric, effects);
      const sourceInvalid = Boolean(source) && !isSourceAllowed(String(source), effects);

      if (metricInvalid) {
        onChange(parameter.key, undefined);
        onConstraintClearedParameterValue?.({
          parameterLabel: parameter.label,
          description: effects.activeDescriptions?.join(' '),
          change: {
            kind: 'cleared',
            from: labelForValue(parameter.options?.metrics, metric)
          }
        });
      } else if (sourceInvalid) {
        onChange(parameter.key, { ...metricConfig, source: undefined });
        onConstraintClearedParameterValue?.({
          parameterLabel: parameter.label,
          description: effects.activeDescriptions?.join(' '),
          change: {
            kind: 'cleared',
            from: labelForValue(parameter.options?.sources, source as string)
          }
        });
      }
    }

    if (parameter.type === ParameterConfigurationType.SINGLE_SELECT_DROPDOWN) {
      const stringValue = currentValue as string;
      const isInvalid = !isOptionAllowed(
        stringValue,
        effects.allowedValues,
        effects.disallowedValues
      );

      if (isInvalid) {
        onChange(parameter.key, undefined);
        onConstraintClearedParameterValue?.({
          parameterLabel: parameter.label,
          description: effects.activeDescriptions?.join(' '),
          change: { kind: 'cleared', from: labelForValue(parameter.options, stringValue) }
        });
      }
    }

    if (parameter.type === ParameterConfigurationType.MULTI_SELECT_DROPDOWN) {
      if (Array.isArray(currentValue)) {
        const validOpts = optionsWithDisabled(
          parameter.options || [],
          effects.allowedValues,
          effects.disallowedValues
        );
        const validValues = new Set(validOpts.filter(o => !o.disabled).map(o => o.value));
        const filtered = currentValue.filter((v: string) => validValues.has(v));

        if (filtered.length !== currentValue.length) {
          onChange(parameter.key, filtered.length > 0 ? filtered : undefined);
          const removedLabels = currentValue
            .filter((v: string) => !validValues.has(v))
            .map((v: string) => labelForValue(parameter.options, v));
          onConstraintClearedParameterValue?.({
            parameterLabel: parameter.label,
            description: effects.activeDescriptions?.join(' '),
            change: { kind: 'multiRemoved', removed: removedLabels }
          });
        }
      }
    }

    if (effects.hidden && currentValue !== undefined) {
      onChange(parameter.key, undefined);
      // Only attach a `change` for string-valued inputs we can resolve to a label.
      // Other input types (dates, metric configs) fall back to the generic message.
      const hiddenOptions =
        'options' in parameter && Array.isArray(parameter.options) ? parameter.options : undefined;
      const change: ConstraintChange | undefined =
        typeof currentValue === 'string' && hiddenOptions
          ? { kind: 'cleared', from: labelForValue(hiddenOptions, currentValue) }
          : undefined;
      onConstraintClearedParameterValue?.({
        parameterLabel: parameter.label,
        description: effects.activeDescriptions?.join(' '),
        change
      });
    }
  }
};
